import { useState, useEffect } from "react";

import { updateArticleAuthorsOrder } from "@/api/article";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useLocation, useNavigate } from "react-router-dom";
import MoonLoader from "react-spinners/MoonLoader";

import { useArticle } from "@/hooks/server-state/use-article";

const Loading = () => {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-white">
      <MoonLoader color="#0099A5" size={50} />
    </div>
  );
};

const useLastNumberFromUrl = () => {
  const location = useLocation();
  const path = location.pathname;
  const lastSegment = path.substring(path.lastIndexOf("/") + 1);
  return /^\d+$/.test(lastSegment) ? lastSegment : null;
};

const ArticleEditPage = () => {
  const articleId = Number(useLastNumberFromUrl());
  const { data: ArticleType, isLoading: isArticleTypeLoading } = useArticle(
    articleId.toString()
  );
  const navigate = useNavigate();
  const [authors, setAuthors] = useState<any[]>([]);

  useEffect(() => {
    if (ArticleType?.authors) {
      setAuthors(ArticleType.authors);
    }
  }, [ArticleType]);

  if (isArticleTypeLoading) return <Loading />;

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    const reordered = Array.from(authors);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setAuthors(reordered);
  };

  const handleInputChange = (index: number, field: string, value: string) => {
    const updated = [...authors];
    (updated[index] as any)[field] = value;
    setAuthors(updated);
  };

  const handleSave = async () => {
    // ساخت payload برای بک
    const payload = authors.map((a, idx) => ({
      userIdentifierEn: a.userIdentifierEn,
      order: idx + 1,
      affiliation: a.affiliation,
      name: a.name
    }));

    try {
      await updateArticleAuthorsOrder(articleId, payload);
      console.log("Changes saved successfully");
      handleGoBack();
    } catch (err) {
      console.error("Error saving changes:", err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-[900px] overflow-auto rounded-lg bg-white p-6 shadow-lg">
        <h2 className="mb-4 text-xl font-bold">ویرایش نویسندگان</h2>

        {/* جدول */}
        <div className="grid grid-cols-3 border-b bg-gray-100 font-semibold">
          <div className="p-2">ردیف</div>
          <div className="p-2">نام</div>
          <div className="p-2">Affiliation</div>
        </div>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="authorsTable">
            {(provided: any) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {authors.map((author: any, index) => (
                  <Draggable
                    key={index}
                    draggableId={String(index)}
                    index={index}
                  >
                    {(provided: any) => (
                      <div
                        className="grid cursor-move grid-cols-3 border-b bg-white hover:bg-gray-50"
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <div className="p-2">{index + 1}</div>
                        <div className="p-2">{author.name}</div>
                        <div className="p-2">
                          <input
                            type="text"
                            value={author.affiliation || ""}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "affiliation",
                                e.target.value
                              )
                            }
                            className="w-full rounded border p-1"
                          />
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {/* دکمه‌ها */}
        <div className="mt-6 flex justify-center gap-4">
          <button
            onClick={handleSave}
            className="rounded bg-primary px-4 py-2 text-white"
          >
            ذخیره تغییرات
          </button>
          <button
            onClick={handleGoBack}
            className="rounded bg-red-400 px-4 py-2 text-white hover:bg-red-500"
          >
            بازگشت
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArticleEditPage;
